/**
 * meta/state.js — the save slices the meta systems own, plus the calendar.
 *
 * Two things live here because everything else in `meta/` depends on them:
 *
 * 1. **Save access.** Prize sets, the login streak, weekly challenges and
 *    weekly bests are all slices of `save.data`. Each accessor lazily creates
 *    its slice, so a save written before this module existed still works and
 *    `migrate()`'s deep merge never has to know about a half-built document.
 *
 * 2. **The calendar.** Every date-driven rule in the meta layer — seasonal set
 *    availability, the ISO week that seeds challenges and leaderboards, the
 *    login streak — reads `save.today()` and NEVER `Date.now()` directly.
 *    `save.today()` includes the test-mode day offset, so `__cc.time.advanceDays`
 *    moves seasons, weeks and streaks together exactly as real time would.
 *
 * @module meta/state
 */

import { save } from '../core/save.js';
import { mulberry32 } from '../core/testmode.js';

const MS_PER_DAY = 86_400_000;

// ─────────────────────────────────────────────────────────────────────────────
// Calendar — all derived from save.today(), never from the wall clock
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The game's current day as a UTC Date.
 * @param {number} [dayIndex] Defaults to `save.today()`.
 * @returns {Date} Midnight UTC on that day.
 */
export function dayDate(dayIndex = save.today()) {
  return new Date(dayIndex * MS_PER_DAY);
}

/**
 * ISO-8601 week containing a day. Weeks start Monday, and week 1 is the one
 * containing the first Thursday of the year — which is why this pivots to
 * Thursday before counting.
 *
 * @param {number} [dayIndex] Defaults to `save.today()`.
 * @returns {{ year: number, week: number, key: string }}
 */
export function isoWeek(dayIndex = save.today()) {
  const d = dayDate(dayIndex);
  const thursday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = thursday.getUTCDay() || 7; // Mon=1 … Sun=7
  thursday.setUTCDate(thursday.getUTCDate() + 4 - dayNum);
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((thursday.getTime() - yearStart) / MS_PER_DAY + 1) / 7);
  const year = thursday.getUTCFullYear();
  return { year, week, key: `${year}-W${String(week).padStart(2, '0')}` };
}

/** @param {number} [dayIndex] @returns {string} ISO week key, e.g. `2026-W31`. */
export function weekKey(dayIndex = save.today()) {
  return isoWeek(dayIndex).key;
}

/**
 * Calendar quarter, which is what gates the seasonal prize sets (GDD 8.5).
 * @param {number} [dayIndex] Defaults to `save.today()`.
 * @returns {'Q1'|'Q2'|'Q3'|'Q4'}
 */
export function quarter(dayIndex = save.today()) {
  return `Q${Math.floor(dayDate(dayIndex).getUTCMonth() / 3) + 1}`;
}

/** @param {number} [dayIndex] @returns {number} Month index 0–11, UTC. */
export function monthIndex(dayIndex = save.today()) {
  return dayDate(dayIndex).getUTCMonth();
}

/**
 * Days until a quarter comes round again — used for "returns in Q4" labels.
 * @param {'Q1'|'Q2'|'Q3'|'Q4'} targetQuarter
 * @param {number} [dayIndex]
 * @returns {number} Whole days, 0 if that quarter is live now.
 */
export function daysUntilQuarter(targetQuarter, dayIndex = save.today()) {
  if (quarter(dayIndex) === targetQuarter) return 0;
  const target = Number(String(targetQuarter).slice(1));
  const d = dayDate(dayIndex);
  const year = d.getUTCFullYear();
  const startMonth = (target - 1) * 3;
  let start = Date.UTC(year, startMonth, 1);
  if (start <= d.getTime()) start = Date.UTC(year + 1, startMonth, 1);
  return Math.round((start - d.getTime()) / MS_PER_DAY);
}

// ─────────────────────────────────────────────────────────────────────────────
// Deterministic seeding
//
// Leaderboard rivals and the weekly challenge draw must be reproducible from
// (subject, week) ALONE — not from however many times gameplay happened to pull
// from the global generator first. So they get their own mulberry32 stream from
// a hashed key rather than calling rng(). Same generator, same determinism
// guarantee, no coupling to call order.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * FNV-1a over a string.
 * @param {string} str
 * @returns {number} u32 seed.
 */
export function hashSeed(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A reproducible generator for a named subject.
 * @param {string} key e.g. `rivals:tin-can-alley:2026-W31`
 * @returns {() => number} [0, 1)
 */
export function seededRng(key) {
  return mulberry32(hashSeed(key));
}

/**
 * Fisher-Yates using a supplied generator, so the shuffle is reproducible.
 * @template T @param {T[]} arr @param {() => number} gen @returns {T[]} A new array.
 */
export function seededShuffle(arr, gen) {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(gen() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Save slices
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A set's stored progress, created on first access.
 * @param {string} setId
 * @returns {{ pieces: string[], completed: boolean }}
 */
export function setSlice(setId) {
  if (!save.data.sets || typeof save.data.sets !== 'object') save.data.sets = {};
  let slice = save.data.sets[setId];
  if (!slice || typeof slice !== 'object') {
    slice = { pieces: [], completed: false };
    save.data.sets[setId] = slice;
  }
  if (!Array.isArray(slice.pieces)) slice.pieces = [];
  return slice;
}

/**
 * Login streak state (GDD 8.7). `calendarsCompleted` drives which monthly
 * exclusive the day-30 payout grants.
 * @returns {{ streak: number, lastClaimDay: number, claimedDays: number[],
 *             calendarsCompleted: number }}
 */
export function dailySlice() {
  if (!save.data.daily || typeof save.data.daily !== 'object') {
    save.data.daily = { streak: 0, lastClaimDay: -1, claimedDays: [] };
  }
  const d = save.data.daily;
  if (!Array.isArray(d.claimedDays)) d.claimedDays = [];
  if (typeof d.streak !== 'number') d.streak = 0;
  if (typeof d.lastClaimDay !== 'number') d.lastClaimDay = -1;
  if (typeof d.calendarsCompleted !== 'number') d.calendarsCompleted = 0;
  return d;
}

/**
 * Weekly challenge state. Rebuilt by challenges.js whenever `key` goes stale.
 * @returns {{ key: string, list: object[], counters: object }}
 */
export function challengeSlice() {
  if (!save.data.challenges || typeof save.data.challenges !== 'object') {
    save.data.challenges = { key: '', list: [], counters: {} };
  }
  const c = save.data.challenges;
  if (!Array.isArray(c.list)) c.list = [];
  if (!c.counters || typeof c.counters !== 'object') c.counters = {};
  return c;
}

/**
 * Per-ISO-week best score per stall, which is what the weekly leaderboard
 * ranks. Resets itself when the week key changes (GDD 7.6: Monday 00:00).
 * @returns {{ key: string, best: Record<string, number> }}
 */
export function weeklySlice() {
  if (!save.data.weekly || typeof save.data.weekly !== 'object') {
    save.data.weekly = { key: weekKey(), best: {} };
  }
  const w = save.data.weekly;
  if (!w.best || typeof w.best !== 'object') w.best = {};
  const now = weekKey();
  if (w.key !== now) {
    w.key = now;
    w.best = {};
    save.markDirty();
  }
  return w;
}

/** @returns {boolean} GDD 12.4 IAP master switch; defaults on. */
export function iapEnabled() {
  if (!save.data.settings || typeof save.data.settings !== 'object') return true;
  return save.data.settings.iapEnabled !== false;
}

/** @param {boolean} on @returns {boolean} The new value. */
export function setIapEnabled(on) {
  if (!save.data.settings || typeof save.data.settings !== 'object') save.data.settings = {};
  save.data.settings.iapEnabled = !!on;
  save.markDirty();
  return save.data.settings.iapEnabled;
}
